/**
 * Addresses that moved. A search engine, a bookmark or a link somewhere else may still point at the
 * old address; the host answers it with a permanent redirect (301) to the new one, so the page keeps
 * its search ranking and nobody lands on a 404. The single-page app has the same rules for
 * client-side visits (`app/route-config.tsx`, `app/routes/applications-moved.tsx`,
 * `app/routes/hindi-moved.tsx`).
 *
 *   · "Where to Use" (D-356): `/applications…` → `/where-to-use…`
 *   · the retired Hindi site (D-358): `/hi` → `/`, `/hi/<page>` → `/<page>`
 *
 * A pure module (no `@/` imports): the hosting files (`scripts/headers.ts`), the preview server
 * (`serving.ts`) and the tests all read this one list.
 */

/** A whole section: `from` and everything below it moves to `to` (the same tail kept). */
export interface MovedSection {
  from: string;
  to: string;
}

export const MOVED_SECTIONS: readonly MovedSection[] = [
  // "Where to Use" is the name of the page in the menu; the address follows the name
  { from: '/applications', to: '/where-to-use' },
];

/**
 * The 14 catalogue stones had short addresses (`/shop/crystal-green`); each now says what it is
 * (`/shop/crystal-green-granite-slab`, D-364). The old address answers with a permanent redirect.
 */
export const RENAMED_STONES: Readonly<Record<string, string>> = {
  'p-white': 'p-white-granite-slab',
  'crystal-green': 'crystal-green-granite-slab',
  'majestic-black': 'majestic-black-granite-countertop',
  'steel-grey': 'steel-grey-granite-tiles',
  'smoke-grey': 'smoke-grey-granite-tiles',
  'smoke-black': 'smoke-black-granite-countertop',
  'black-markino': 'black-markino-granite-slab',
  'silver-pearl': 'silver-pearl-granite-countertop',
  'tan-brown': 'tan-brown-granite-slab',
  'coffee-brown': 'coffee-brown-granite-paving-stone',
  'chima-pink': 'chima-pink-granite-slab',
  'multicolor-red': 'multicolor-red-granite-slab',
  'imperial-red': 'imperial-red-granite-slab',
  'jhansi-red': 'jhansi-red-granite-slab',
};

/** One single address that moved (not the pages below it). */
export interface MovedPage {
  from: string;
  to: string;
}

/**
 * The Monuments guide was removed (D-361): both of its old addresses answer with the "Where to Use" page. They are
 * listed before the sections, because a host uses the first rule that matches.
 */
export const MOVED_PAGES: readonly MovedPage[] = [
  ...Object.entries(RENAMED_STONES).map(([from, to]) => ({
    from: `/shop/${from}`,
    to: `/shop/${to}`,
  })),
  { from: '/applications/monuments', to: '/where-to-use' },
  { from: '/where-to-use/monuments', to: '/where-to-use' },
  // the Privacy policy and the Terms of use were replaced by the one Legal Disclaimer (D-372)
  { from: '/privacy', to: '/legal-disclaimer' },
  { from: '/terms', to: '/legal-disclaimer' },
];

/** The path prefix of the Hindi site, which no longer exists (D-358). */
export const RETIRED_PREFIX = '/hi';

const RETIRED_PATH = /^\/hi(?=\/|$)/;

/** One redirect as a host rule: `from` may end in `/*`, and `to` then ends in `/:splat`. */
export interface LegacyRedirect {
  from: string;
  to: string;
}

/** `/hi` → `/`, `/hi/shop` → `/shop`; any other address unchanged. */
export function withoutRetiredPrefix(pathname: string): string {
  if (!RETIRED_PATH.test(pathname)) return pathname;
  return pathname.slice(RETIRED_PREFIX.length) || '/';
}

/**
 * Every redirect rule, most specific first (hosts use the first rule that matches): a section and
 * everything below it, then the same under the retired `/hi` prefix straight to the final address
 * (one hop, not two), then `/hi` itself and everything below it.
 *
 *   `/applications/monuments` → `/where-to-use`, `/where-to-use/monuments` → `/where-to-use`,
 *   `/applications` → `/where-to-use`, `/applications/*` → `/where-to-use/:splat`,
 *   `/hi/applications…` → `/where-to-use…`, `/hi` → `/`, `/hi/*` → `/:splat`
 */
export function legacyRedirects(
  sections: readonly MovedSection[] = MOVED_SECTIONS,
  pages: readonly MovedPage[] = MOVED_PAGES,
): LegacyRedirect[] {
  const rulesUnder = (prefix: string): LegacyRedirect[] =>
    sections.flatMap(({ from, to }) => [
      { from: `${prefix}${from}`, to },
      { from: `${prefix}${from}/*`, to: `${to}/:splat` },
    ]);
  return [
    ...pages.map(({ from, to }) => ({ from, to })),
    ...rulesUnder(''),
    ...rulesUnder(RETIRED_PREFIX),
    { from: RETIRED_PREFIX, to: '/' },
    { from: `${RETIRED_PREFIX}/*`, to: '/:splat' },
  ];
}

/**
 * Where a request for `pathname` (decoded, no query string) must be sent, or `null` when the address
 * has not moved. Matches case-sensitively and exactly like the host rules do.
 */
export function legacyRedirectFor(
  pathname: string,
  sections: readonly MovedSection[] = MOVED_SECTIONS,
  pages: readonly MovedPage[] = MOVED_PAGES,
): string | null {
  const neutral = withoutRetiredPrefix(pathname);
  for (const { from, to } of pages) if (neutral === from) return to;
  for (const { from, to } of sections) {
    if (neutral === from) return to;
    if (neutral.startsWith(`${from}/`)) return `${to}${neutral.slice(from.length)}`;
  }
  return neutral === pathname ? null : neutral;
}
